import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ReactFlowProvider } from "@xyflow/react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import PlayIcon from "@mui/icons-material/PlayArrowRounded";
import BackIcon from "@mui/icons-material/ArrowBackRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { LoadingState, ErrorState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { PipelineCanvas } from "@/components/pipeline-canvas/pipeline-canvas";
import { StepInspector } from "@/components/pipeline-canvas/inspector";
import { AiEditDialog } from "@/components/pipeline-canvas/ai-edit-dialog";
import { usePipelineEditor } from "@/components/pipeline-canvas/use-editor";
import { type PipelineDetail, usePipeline, usePipelineMutations } from "@/api/pipelines";
import { useConnectorNames } from "@/api/connectors";
import { useAiFeature } from "@/api/ai";

export const Route = createFileRoute("/_app/pipelines/$pipelineId/edit")({
  component: PipelineEditorRoute,
});

function PipelineEditorRoute() {
  const { pipelineId } = Route.useParams();
  const { data: p, isLoading, error, refetch } = usePipeline(pipelineId);

  if (isLoading) return <LoadingState />;
  if (error || !p) return <ErrorState error={error ?? "Not found"} onRetry={() => refetch()} />;

  // Remount when the pipeline changes so the editor re-seeds from its steps.
  return <PipelineEditorBody key={p.id} pipeline={p} />;
}

function PipelineEditorBody({ pipeline: p }: { pipeline: PipelineDetail }) {
  const navigate = useNavigate();
  const toast = useToast();
  const names = useConnectorNames();
  const m = usePipelineMutations(p.id);
  const editor = usePipelineEditor(p.steps ?? []);
  const aiEditEnabled = useAiFeature("edit");
  const [aiOpen, setAiOpen] = useState(false);

  const selected = editor.steps.find((s) => s.nodeKey === editor.selectedKey);
  const selectedIndex = editor.steps.findIndex((s) => s.nodeKey === editor.selectedKey);

  async function onSave() {
    const payload = editor.toPayload();
    await m.updateSteps.mutateAsync(payload);
    editor.reset(payload);
    toast.success("Pipeline saved");
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100dvh - 60px)" }}>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: "center", px: 3, py: 1, borderBottom: 1, borderColor: "divider" }}
      >
        <Button
          size="small"
          startIcon={<BackIcon />}
          onClick={() => navigate({ to: "/pipelines/$pipelineId", params: { pipelineId: p.id } })}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ flex: 1 }} noWrap>
          {p.name}
        </Typography>
        {editor.dirty ? <Chip size="small" color="warning" label="Unsaved" /> : null}
        {aiEditEnabled ? (
          <Button size="small" startIcon={<AutoAwesomeIcon />} onClick={() => setAiOpen(true)}>
            Edit with AI
          </Button>
        ) : null}
        <Button
          size="small"
          startIcon={<PlayIcon />}
          onClick={async () => {
            await m.run.mutateAsync(p.id);
            toast.success("Run triggered");
          }}
        >
          Run
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!editor.dirty || m.updateSteps.isPending}
          onClick={onSave}
        >
          Save
        </Button>
      </Stack>

      <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
        <ReactFlowProvider>
          <PipelineCanvas
            editor={editor}
            sourceName={names[p.sourceConnectorId ?? ""] ?? "source"}
            destName={names[p.destConnectorId ?? ""] ?? "destination"}
          />
        </ReactFlowProvider>
        {selected ? (
          <StepInspector
            key={selected.nodeKey}
            step={selected}
            editor={editor}
            index={selectedIndex}
            count={editor.steps.length}
          />
        ) : null}
      </Box>

      <AiEditDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        pipelineId={p.id}
        onApply={(steps) => {
          editor.replaceSteps(steps);
          toast.success("AI change applied — review, then Save");
        }}
      />
    </Box>
  );
}
